import React from 'react';
import { cn } from './cn';

/**
 * The app's button.
 *
 * `min-h-[var(--tap-target)]` is the important bit: 44px so a thumb can find
 * it, tightening to 38px only on a true pointer device where precision is
 * higher and vertical space is scarcer.
 *
 * Note there are no hand-written `@media (hover: hover)` guards here. Tailwind
 * v4 already wraps every `hover:` variant in one, so these hover styles do not
 * stick after a tap on a touch screen.
 */
const VARIANTS = {
  primary: 'bg-accent text-accent-ink hover:bg-accent-hover',
  secondary:
    'bg-surface-raised border-line-strong text-ink hover:bg-surface-overlay hover:border-line-loud',
  ghost: 'bg-transparent text-ink-muted hover:bg-surface-raised hover:text-ink',
  danger:
    'bg-transparent border-line-strong text-danger hover:bg-danger-tint hover:border-danger',
  // Borderless destructive action, for inside a card where a bordered button
  // would compete with the card's own edge. A separate variant rather than
  // `variant="ghost" className="text-danger"`, because cn() does not resolve
  // Tailwind conflicts — the ghost variant's text-ink-muted and an appended
  // text-danger sit in the same layer and source order decides, which silently
  // dropped the red here once already.
  dangerGhost: 'bg-transparent text-danger hover:bg-danger-tint',
};

const SIZES = {
  sm: 'min-h-[var(--tap-target)] sm:min-h-[34px] px-3.5 text-caption',
  md: 'min-h-[var(--tap-target)] sm:min-h-[38px] px-[18px] text-body-sm',
  lg: 'min-h-[50px] px-6 text-body',
};

export const Button = React.forwardRef(function Button(
  { as: Tag = 'button', variant = 'secondary', size = 'md', icon, block, className, type, ...props },
  ref
) {
  return (
    <Tag
      ref={ref}
      // A <button> inside a form defaults to type=submit, which has submitted
      // more forms by accident than it has on purpose.
      type={Tag === 'button' ? type || 'button' : type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-pill border border-transparent',
        'font-medium leading-none whitespace-nowrap select-none no-underline',
        'transition-colors duration-[120ms] ease-[cubic-bezier(.22,.61,.36,1)]',
        'disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none',
        SIZES[size],
        VARIANTS[variant],
        // Icon-only: keep the target square rather than a 20px-wide sliver
        // that happens to be tall enough.
        icon && 'min-w-[var(--tap-target)] sm:min-w-[38px] px-2.5',
        block && 'w-full sm:w-auto',
        className
      )}
      {...props}
    />
  );
});

export default Button;
